import React, { useContext, useEffect, useState } from 'react';
import _ from 'lodash';

import { FetchContext, HttpMethods } from './FetchContext';
import { useAggregatedState } from '../../helpers';

const utf8 = require('utf8');

const compileRoute = (route, params) => {
    if (params) {
        const props = Object.getOwnPropertyNames(params);
        if (props && props.length > 0) {
            route = `${route}?`
            props.filter(x => params[x] !== null && params[x] !== undefined).forEach(prop => route = `${route}${prop}=${params[prop]}&`);
            route = route.substr(0, route.length - 1);
        }
    }
    return route;
}

export const ApiRoutes = {
    Role: {
        GetRoles: (params) => compileRoute("/api/Role/GetRoles", params),
        GetRole: (params) => compileRoute("/api/Role/GetRole", params),
        AddRoleToUser: () => "/api/Role/AddRoleToUser",
        AddRolesToUser: () => "/api/Role/AddRolesToUser",
        AssignRoleToUser: () => "/api/Role/AssignRoleToUser",
        AssignRolesToUser: () => "/api/Role/AssignRolesToUser",
        UpdateRoleForUser: () => "/api/Role/UpdateRoleForUser",
        RevokeRoleForUser: (params) => compileRoute("/api/Role/RevokeRoleForUser", params),
        RevokeAllRolesForUser: (params) => compileRoute("/api/Role/RevokeAllRolesForUser", params)
    },
    User: {
        GetUser: (params) => compileRoute("/api/User/GetUser", params),
        Me: {
            GetMe: (params) => compileRoute("/api/User/Me", params),
        }
    },
    Table: {
        GetManagedTables: (params) => compileRoute("/api/Table/GetManagedTables", params),
        GetManagedTable: (params) => compileRoute("/api/Table/GetManagedTable", params),
        GetLogsTable: (params) => compileRoute("/api/Table/GetLogsTable", params),
        ToggleTableAvailability: () => "/api/Table/ToggleTableAvailability",
        ToggleTableAllowImport: () => "/api/Table/ToggleTableAllowImport",
        ToggleTableClearOnImport: () => "/api/Table/ToggleTableClearOnImport",
        UpdateTable: () => "/api/Table/UpdateTable",
        SynchronizeExterenalTables: () => "/api/Table/SynchronizeExterenalTables",
        Me: {
            GetAssignedTable: (params) => compileRoute("/api/Table/Me/GetAssignedTable", params),
            GetDataExportInfo: (params) => compileRoute("/api/Table/Me/GetDataExportInfo", params),
            UpdateTableState: () => "/api/Table/Me/UpdateTableState",
            UpdateTableContent: () => "/api/Table/Me/UpdateTableContent",
            EnqueueTableContentImport: (params) => compileRoute("/api/Table/Me/EnqueueTableContentImport", params),
            EnqueueTableContentExport: () => "/api/Table/Me/EnqueueTableContentExport",
            DeleteAllFiltered: () => "/api/Table/Me/DeleteAllFiltered"
        }
    },
    Graph: {
        GetUsers: (params) => compileRoute("/api/Graph/GetUsers", params)
    }
}

export const RestApiContext = React.createContext(null);

const { Provider } = RestApiContext;
export const RestApiProvider = ({ ...props }) => {
    const { fetchAuthorized } = useContext(FetchContext);
    const [state, setState] = useAggregatedState({
    });

    const sleep = (ms) => {
        return new Promise(resolve => setTimeout(resolve, ms));
    }

    const getRolesAsync = async (onlyManagement, withUsers) => {
        const r = await fetchAuthorized(ApiRoutes.Role.GetRoles({ onlyManagement, withUsers }), {
            method: HttpMethods.GET
        });
        const roles = await r.json();
        return roles;
    }

    const getRoleAsync = async (roleId, withUsers) => {
        const r = await fetchAuthorized(ApiRoutes.Role.GetRole({ roleId, withUsers }), {
            method: HttpMethods.GET
        });
        const role = await r.json();
        return role;
    }

    const getUserAsync = async (userId) => {
        const r = await fetchAuthorized(ApiRoutes.User.GetUser({ userId }), {
            method: HttpMethods.GET
        });
        const user = await r.json();
        return user;
    }

    const getMeAsync = async (userId) => {
        const r = await fetchAuthorized(ApiRoutes.User.Me.GetMe(), {
            method: HttpMethods.GET
        });
        const user = await r.json();
        return user;
    }

    const addRoleToUserAsync = async ({ roleId, userId, tableId }) => {
        let body = { roleId, userId, tableId };
        let formBody = btoa(utf8.encode(JSON.stringify(body)));

        const r = await fetchAuthorized(ApiRoutes.Role.AddRoleToUser(), {
            method: HttpMethods.POST,
            body: `=${formBody}`,
            collectErrorDetails: true
        });
        const role = await r.json();
        return role;
    }

    const addRolesToUserAsync = async ({ roleIds, userId, tableId }) => {
        let body = { roleIds, userId, tableId };
        let formBody = btoa(utf8.encode(JSON.stringify(body)));

        const r = await fetchAuthorized(ApiRoutes.Role.AddRolesToUser(), {
            method: HttpMethods.POST,
            body: `=${formBody}`,
            collectErrorDetails: true
        });
        const roles = await r.json();
        return roles;
    }

    const assignRoleToUserAsync = async ({ roleId, userId, tableIds }) => {
        let body = { roleId, userId, tableIds };
        let formBody = btoa(utf8.encode(JSON.stringify(body)));

        const r = await fetchAuthorized(ApiRoutes.Role.AssignRoleToUser(), {
            method: HttpMethods.POST,
            body: `=${formBody}`,
            collectErrorDetails: true
        });
        const role = await r.json();
        return role;
    }

    const assignRolesToUserAsync = async ({ roleIds, userId, tableIds }) => {
        let body = { roleIds, userId, tableIds };
        let formBody = btoa(utf8.encode(JSON.stringify(body)));

        const r = await fetchAuthorized(ApiRoutes.Role.AssignRolesToUser(), {
            method: HttpMethods.POST,
            body: `=${formBody}`,
            collectErrorDetails: true
        });
        const roles = await r.json();
        return roles;
    }

    const revokeRoleForUserAsync = async (roleId, userId, tableIds) => {
        let body = { roleId, userId, tableIds };
        let formBody = btoa(utf8.encode(JSON.stringify(body)));

        const r = await fetchAuthorized(ApiRoutes.Role.RevokeRoleForUser(), {
            method: HttpMethods.DELETE,
            body: `=${formBody}`,
            collectErrorDetails: true
        });
        const role = await r.json();
        return role;
    }

    const revokeAllRolesForUserAsync = async (userId, tableIds) => {
        let body = { userId, tableIds };
        let formBody = btoa(utf8.encode(JSON.stringify(body)));

        const r = await fetchAuthorized(ApiRoutes.Role.RevokeAllRolesForUser(), {
            method: HttpMethods.DELETE,
            body: `=${formBody}`,
            collectErrorDetails: true
        });
    }

    const getManagedTablesAsync = async (tableName, withUsers, pageIndex, pageSize) => {
        const r = await fetchAuthorized(ApiRoutes.Table.GetManagedTables({ tableName, withUsers, pageIndex, pageSize }), {
            method: HttpMethods.GET
        });
        const pagedList = await r.json();
        return pagedList;
    }

    const getManagedTableAsync = async (tableId, withUsers, withSchema, withData, sortAndFilter) => {
        const r = await fetchAuthorized(ApiRoutes.Table.GetManagedTable({ tableId, withUsers, withSchema, withData, sortAndFilter }), {
            method: HttpMethods.GET
        });
        const table = await r.json();
        return table;
    }

    const getLogsTableAsync = async (withData, sortAndFilter) => {
        const r = await fetchAuthorized(ApiRoutes.Table.GetLogsTable({ withData, sortAndFilter }), {
            method: HttpMethods.GET
        });
        const table = await r.json();
        return table;
    }

    const getAssignedTableAsync = async (tableId, withUsers, withSchema, withData, sortAndFilter) => {
        const r = await fetchAuthorized(ApiRoutes.Table.Me.GetAssignedTable({ tableId, withUsers, withSchema, withData, sortAndFilter }), {
            method: HttpMethods.GET
        });
        const table = await r.json();
        return table;
    }

    const getDataExportInfoAsync = async (token) => {
        const r = await fetchAuthorized(ApiRoutes.Table.Me.GetDataExportInfo({ token }), {
            method: HttpMethods.GET
        });
        const exportInfo = await r.json();
        return exportInfo;
    }

    const updateTableAsync = async ({ tableId, isDisabled, allowImport, clearOnImport, description }) => {
        let body = { tableId, isDisabled, allowImport, clearOnImport, description };
        let formBody = btoa(utf8.encode(JSON.stringify(body)));

        const r = await fetchAuthorized(ApiRoutes.Table.UpdateTable(), {
            method: HttpMethods.POST,
            body: `=${formBody}`,
            collectErrorDetails: true
        });

        const table = await r.json();
        return table;
    }

    const toggleTableAvailabilityAsync = async ({ tableId, isDisabled }) => {

        let body = { tableId, isDisabled };
        let formBody = btoa(utf8.encode(JSON.stringify(body)));

        const r = await fetchAuthorized(ApiRoutes.Table.ToggleTableAvailability(), {
            method: HttpMethods.POST,
            body: `=${formBody}`,
            collectErrorDetails: true
        });
        const table = await r.json();
        return table;
    }

    const toggleTableAllowImportAsync = async ({ tableId, allowImport }) => {

        let body = { tableId, allowImport };
        let formBody = btoa(utf8.encode(JSON.stringify(body)));

        const r = await fetchAuthorized(ApiRoutes.Table.ToggleTableAllowImport(), {
            method: HttpMethods.POST,
            body: `=${formBody}`,
            collectErrorDetails: true
        });
        const table = await r.json();
        return table;
    }

    const toggleTableClearOnImportAsync = async ({ tableId, clearOnImport }) => {

        let body = { tableId, clearOnImport };
        let formBody = btoa(utf8.encode(JSON.stringify(body)));

        const r = await fetchAuthorized(ApiRoutes.Table.ToggleTableClearOnImport(), {
            method: HttpMethods.POST,
            body: `=${formBody}`,
            collectErrorDetails: true
        });
        const table = await r.json();
        return table;
    }

    const syncExternalTablesAsync = async () => {
        const r = await fetchAuthorized(ApiRoutes.Table.SynchronizeExterenalTables(), {
            method: HttpMethods.POST
        });
    }

    const getGraphUsersAsync = async (userFullPartialName) => {
        const r = await fetchAuthorized(ApiRoutes.Graph.GetUsers({ userFullPartialName }), {
            method: HttpMethods.GET
        });
        const users = await r.json();
        return users;
    }

    const updateTableStateAsync = async ({ tableId, state }) => {
        let body = { tableId, state };
        let formBody = btoa(utf8.encode(JSON.stringify(body)));

        const r = await fetchAuthorized(ApiRoutes.Table.Me.UpdateTableState(), {
            method: HttpMethods.POST,
            body: `=${formBody}` 
        });
    }

    const updateTableContentAsync = async ({ tableId, transaction }) => {
        let body = { tableId, transaction };
        let formBody = btoa(utf8.encode(JSON.stringify(body)));

        const r = await fetchAuthorized(ApiRoutes.Table.Me.UpdateTableContent(), {
            method: HttpMethods.POST,
            body: `=${formBody}`
        });
        const updateResult = await r.json();
        return updateResult;
    }

    const deleteAllFiltered = async (tableId, sortAndFilter) => {
        sortAndFilter = btoa(JSON.stringify({ filterModel: sortAndFilter }));
        let body = { tableId, sortAndFilter };
        let formBody = btoa(utf8.encode(JSON.stringify(body)));

        const r = await fetchAuthorized(ApiRoutes.Table.Me.DeleteAllFiltered(), {
            method: HttpMethods.POST,
            body: `=${formBody}`
        });
    }

    const enqueueTableImportAsync = async ({ tableId, data, clearOnImport }) => {
        const r = await fetchAuthorized(ApiRoutes.Table.Me.EnqueueTableContentImport({ tableId, clearOnImport }), {
            method: HttpMethods.POST,
            body: data
        });
    }

    const enqueueTableExportAsync = async ({ tableId, sortAndFilter }) => {
        
        let body = { tableId, sortAndFilter };
        let formBody = btoa(utf8.encode(JSON.stringify(body)));

        const r = await fetchAuthorized(ApiRoutes.Table.Me.EnqueueTableContentExport(), {
            method: HttpMethods.POST,
            body: `=${formBody}`
        });
    }

    return (
        <Provider value={{
            getRolesAsync,
            getRoleAsync,
            getUserAsync,
            getMeAsync,
            addRoleToUserAsync,
            addRolesToUserAsync,
            assignRoleToUserAsync,
            assignRolesToUserAsync,
            revokeRoleForUserAsync,
            revokeAllRolesForUserAsync,
            getManagedTablesAsync,
            getManagedTableAsync,
            getLogsTableAsync,
            getAssignedTableAsync,
            getDataExportInfoAsync,
            toggleTableAvailabilityAsync,
            toggleTableAllowImportAsync,
            toggleTableClearOnImportAsync,
            updateTableAsync,
            syncExternalTablesAsync,
            getGraphUsersAsync,
            updateTableStateAsync,
            updateTableContentAsync,
            enqueueTableImportAsync,
            enqueueTableExportAsync,
            deleteAllFiltered
        }}>
            {props.children}
        </Provider>
    );
}